/**
 * Exportar a Excel (Req. 28).
 *
 * Solo lee. Arma las siete hojas con lo que hay en la base y, aparte,
 * convierte esas hojas en un archivo .xlsx. La librería se carga con un
 * import dinámico (design.md, 3): así no entra al paquete que se descarga al
 * abrir la app y solo se baja cuando de verdad se exporta.
 *
 * El Excel no es un respaldo y no se puede volver a importar (Req. 28.3): el
 * respaldo es el archivo de Más → Respaldos.
 */

import { db, leerSettings } from './schema';
import { historialMetasManualesDe } from './metas';
import type { Exercise, Plan, Scale, Workout } from './types';
import { etapaDe, metasDelDia, metasManualesVigentes } from '@/domain/etapas';
import {
  hojasDeExcel,
  nombreDeArchivoExcel,
  resumenDeHojas,
  type AyunoParaExcel,
  type DatosParaExcel,
  type DiaParaExcel,
  type HojaDeExcel,
  type PesajeParaExcel,
  type SerieParaExcel,
} from '@/domain/excel';
import { sumar } from '@/domain/nutricion';
import { fechaLocalDe } from '@/domain/time';
import type { FechaLocal } from '@/domain/dates';

export interface ExcelListo {
  nombre: string;
  hojas: HojaDeExcel[];
  /** Cuántas filas de datos lleva cada hoja. */
  resumen: { nombre: string; filas: number }[];
  /** El total de filas de datos: 0 = no hay nada que exportar. */
  filas: number;
}

const porFecha = (a: { fecha: FechaLocal }, b: { fecha: FechaLocal }) => a.fecha.localeCompare(b.fecha);

/**
 * LECTURA PURA: las hojas del Excel con todo lo registrado (Req. 28.1), de lo
 * más antiguo a lo más reciente. Los pesos y las medidas van en la unidad
 * preferida y las fechas como texto (Req. 28.2).
 */
export async function prepararExcel(ahora: Date = new Date()): Promise<ExcelListo> {
  const [ajustes, workouts, items, series, ejercicios, pesajes, basculas, medidas, comidas, dias, ayunos, etapas, planes, mantenimientos] =
    await Promise.all([
      leerSettings(),
      db.workouts.toArray(),
      db.workoutExercises.toArray(),
      db.workoutSets.toArray(),
      db.exercises.toArray(),
      db.weighIns.toArray(),
      db.scales.toArray(),
      db.measurements.toArray(),
      db.mealEntries.toArray(),
      db.days.toArray(),
      db.fasts.toArray(),
      db.stages.toArray(),
      db.plans.toArray(),
      db.maintenance.toArray(),
    ]);

  const deEjercicio = new Map<string, Exercise>(ejercicios.map((e) => [e.id, e]));
  const deBascula = new Map<string, Scale>(basculas.map((b) => [b.id, b]));
  const dePlan = new Map<string, Plan>(planes.map((p) => [p.id, p]));
  const deWorkout = new Map<string, Workout>(workouts.map((w) => [w.id, w]));
  const ordenDeItem = new Map(items.map((i) => [i.id, i.orden]));

  // Una fila por serie, en el orden del entrenamiento: su día, su ejercicio y su número
  const seriesParaExcel: SerieParaExcel[] = series
    .flatMap((serie) => {
      const workout = deWorkout.get(serie.workoutId);
      return workout === undefined ? [] : [{ serie, workout, ejercicio: deEjercicio.get(serie.exerciseId) }];
    })
    .sort(
      (a, b) =>
        a.workout.fecha.localeCompare(b.workout.fecha) ||
        a.workout.inicio.localeCompare(b.workout.inicio) ||
        (ordenDeItem.get(a.serie.workoutExerciseId) ?? 0) - (ordenDeItem.get(b.serie.workoutExerciseId) ?? 0) ||
        a.serie.orden - b.serie.orden,
    );

  const pesajesParaExcel: PesajeParaExcel[] = [...pesajes]
    .sort((a, b) => porFecha(a, b) || a.momento.localeCompare(b.momento))
    .map((pesaje) => ({ pesaje, bascula: pesaje.scaleId === null ? undefined : deBascula.get(pesaje.scaleId) }));

  const comidasOrdenadas = [...comidas].sort((a, b) => porFecha(a, b) || a.hora.localeCompare(b.hora));

  // Un renglón por día con registros o con algo anotado, con las metas que tuvo ese día
  const historial = historialMetasManualesDe(ajustes);
  const fechas = [...new Set([...comidas.map((c) => c.fecha), ...dias.map((d) => d.fecha)])].sort();
  const diasParaExcel: DiaParaExcel[] = fechas.map((fecha) => {
    const delDia = comidas.filter((c) => c.fecha === fecha);
    const etapa = etapaDe(etapas, fecha);
    return {
      fecha,
      totales: sumar(delDia),
      dia: dias.find((d) => d.fecha === fecha),
      metas: metasDelDia(fecha, etapas, mantenimientos, metasManualesVigentes(historial, fecha)),
      etapa: etapa === null ? null : etapa.tipo,
    };
  });

  // La meta del día en que terminó, para los ayunos que no traen la suya
  const ayunosParaExcel: AyunoParaExcel[] = [...ayunos]
    .sort((a, b) => a.inicio.localeCompare(b.inicio))
    .map((ayuno) => {
      const fecha = fechaLocalDe(ayuno.fin ?? ayuno.inicio);
      return {
        ayuno,
        metaHoras: metasDelDia(fecha, etapas, mantenimientos, metasManualesVigentes(historial, fecha)).ayunoHoras,
      };
    });

  const datos: DatosParaExcel = {
    series: seriesParaExcel,
    pesajes: pesajesParaExcel,
    medidas: [...medidas].sort((a, b) => porFecha(a, b) || a.tipo.localeCompare(b.tipo)),
    comidas: comidasOrdenadas,
    dias: diasParaExcel,
    ayunos: ayunosParaExcel,
    etapas: [...etapas]
      .sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio) || a.indice - b.indice)
      .map((etapa) => ({ etapa, plan: dePlan.get(etapa.planId) })),
  };

  const hojas = hojasDeExcel(datos, { peso: ajustes.unidadPeso, longitud: ajustes.unidadLongitud });
  const resumen = resumenDeHojas(hojas);
  return {
    nombre: nombreDeArchivoExcel(ahora),
    hojas,
    resumen,
    filas: resumen.reduce((n, h) => n + h.filas, 0),
  };
}

/**
 * Convierte las hojas en un archivo .xlsx. La librería entra con import
 * dinámico, así que esta parte solo se descarga al exportar.
 */
export async function aBlobDeExcel(hojas: readonly HojaDeExcel[]): Promise<Blob> {
  const { default: escribirXlsx } = await import('write-excel-file/browser');
  return escribirXlsx(
    hojas.map((h) => ({ name: h.nombre, data: h.filas })),
    { fontFamily: 'Calibri', fontSize: 11 },
  ).toBlob();
}
